/**
 * Central Event-Bus for SnapHub PWA.
 *
 * Uses the browser-native `EventTarget` API with `CustomEvent` for
 * loosely-coupled communication between services. Exposed as a singleton
 * so every module shares the same bus instance.
 *
 * Supported events:
 * - `auth-state-changed`   — fired when the user logs in or out
 * - `cart-changed`         — fired when the cart contents change
 * - `user-changed`         — fired when user profile data changes
 * - `feedback-submitted`   — fired when a feedback entry is successfully sent
 *
 * Validates: Requirements 45.1
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Union of all known event names on the bus. */
export type EventBusEventName =
  | 'auth-state-changed'
  | 'cart-changed'
  | 'user-changed'
  | 'feedback-submitted'
  | 'customers-changed'
  | 'assortment-changed'
  | 'assortment-dirty-changed'
  | 'assortment-customer-changed'
  | 'assortment-customer-dirty-changed'
  | 'assortment-global-changed'
  | 'assortment-global-dirty-changed'
  | 'master-data-changed'
  | 'user-preferences-changed';

/** Callback signature for event listeners. */
export type EventBusCallback<T = unknown> = (detail: T) => void;

/** Function returned by `on()` that removes the listener when called. */
export type Unsubscribe = () => void;

// ---------------------------------------------------------------------------
// EventBus class
// ---------------------------------------------------------------------------

export class EventBus {
  private readonly target = new EventTarget();

  /**
   * Subscribe to an event.
   *
   * @returns An unsubscribe function. Call it to remove the listener.
   */
  on<T = unknown>(eventName: EventBusEventName, callback: EventBusCallback<T>): Unsubscribe {
    const handler = (e: Event) => {
      callback((e as CustomEvent<T>).detail);
    };

    this.target.addEventListener(eventName, handler);

    return () => {
      this.target.removeEventListener(eventName, handler);
    };
  }

  /**
   * Emit an event with an optional detail payload.
   */
  emit<T = unknown>(eventName: EventBusEventName, detail?: T): void {
    this.target.dispatchEvent(new CustomEvent<T>(eventName, { detail: detail as T }));
  }
}

// ---------------------------------------------------------------------------
// Singleton instance
// ---------------------------------------------------------------------------

/** Global event bus shared across the entire application. */
export const eventBus = new EventBus();
